import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { useSSERefetch } from "../hooks/useSSERefetch";
import StatusBadge from "../components/StatusBadge";
import { LogPanel, type LogLine } from "../components/LogPanel";
import { DeviceActivityFeed } from "../components/DeviceActivityFeed";
import { useIdentify } from "../hooks/useIdentify";

interface DeviceInfo {
  id: string;
  model: string;
  serial: string;
  platform: string;
  os_version: string;
  host: { id: string; hostname: string; ip_address: string } | null;
  host_id: string;
  status: "available" | "busy" | "offline" | "maintenance" | "needs_cleanup" | "claimed" | "installing" | "ready";
  battery_level: number | null;
  has_network: boolean;
  failed_attempts: number;
  adb_tcp_port: number | null;
  last_heartbeat_at: string | null;
  current_claim?: {
    apk_name?: string;
    claimed_at?: string;
    claimed_by?: string;
  };
  recent_executions: Array<{
    id: string;
    test_run_id: string;
    scenario_name: string;
    status: string;
    duration_ms: number | null;
    created_at: string;
  }>;
}

export default function DeviceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: device, loading, error, refetch } = useApi<DeviceInfo>(`/api/devices/${id}`);
  useSSERefetch("/api/events", ["host:heartbeat", "device:status", "device:claimed", "device:released"], refetch);
  const [showReleaseConfirm, setShowReleaseConfirm] = React.useState(false);
  const [releaseError, setReleaseError] = React.useState<string | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = React.useState(false);
  const [removeError, setRemoveError] = React.useState<string | null>(null);

  const handleAction = async (action: "reboot" | "quarantine" | "release" | "cleanup") => {
    try {
      const res = await fetch(`/api/devices/${id}/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ action }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error?.message ?? `Action failed: ${res.status}`);
      }
      refetch();
      return true;
    } catch (err) {
      setReleaseError(err instanceof Error ? err.message : String(err));
      return false;
    }
  };

  const { state: identifyState, identify: handleIdentify } = useIdentify(id);

  const confirmRelease = async () => {
    const ok = await handleAction("release");
    if (ok) {
      setShowReleaseConfirm(false);
      setReleaseError(null);
    }
  };

  // AIDEV-NOTE: Hard-delete the device record. Different from Quarantine
  // (which keeps the device in DB but marks it unavailable) — Remove is for
  // phones that have permanently left the fleet (decommissioned, reassigned
  // to a developer, etc.). The DELETE endpoint refuses if there's an active
  // claim, so the user has to release first. On success we navigate back to
  // the Fleet so the just-deleted detail page doesn't 404 in their face.
  const confirmRemove = async (typedSerial: string) => {
    if (typedSerial !== device?.serial) {
      setRemoveError("Serial does not match.");
      return;
    }
    try {
      const res = await fetch(`/api/devices/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error?.message ?? `Remove failed: ${res.status}`);
      }
      setShowRemoveConfirm(false);
      setRemoveError(null);
      navigate("/");
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : String(err));
    }
  };

  if (loading && !device) {
    return <p style={{ padding: "var(--space-xl)" }}>Loading device...</p>;
  }

  if (error || !device) {
    return (
      <p style={{ padding: "var(--space-xl)", color: "var(--color-error)" }}>
        {error ?? "Device not found."}
      </p>
    );
  }

  const battery = device.battery_level ?? 0;
  const batteryColor =
    battery > 50
      ? "var(--color-success)"
      : battery > 20
        ? "var(--color-warning)"
        : "var(--color-danger)";

  return (
    <div>
      {showReleaseConfirm && (
        <ConfirmReleaseModal
          device={device}
          error={releaseError}
          onCancel={() => {
            setShowReleaseConfirm(false);
            setReleaseError(null);
          }}
          onConfirm={confirmRelease}
        />
      )}

      {showRemoveConfirm && (
        <ConfirmRemoveModal
          device={device}
          error={removeError}
          onCancel={() => {
            setShowRemoveConfirm(false);
            setRemoveError(null);
          }}
          onConfirm={confirmRemove}
        />
      )}

      {/* Breadcrumb */}
      <nav style={{ marginBottom: "var(--space-lg)", fontSize: "var(--font-size-sm)" }}>
        <Link to="/" style={{ color: "var(--color-info)" }}>
          Fleet
        </Link>
        <span style={{ margin: "0 var(--space-xs)", color: "var(--color-text-secondary)" }}>/</span>
        <span>{device.model}</span>
      </nav>

      {/* Device Info + Health Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "var(--space-lg)",
          marginBottom: "var(--space-xl)",
        }}
      >
        {/* Info Card */}
        <div style={cardStyle}>
          <h2 style={cardTitleStyle}>Device Info</h2>
          <div style={infoGridStyle}>
            <InfoRow label="Model" value={device.model} />
            <InfoRow label="Serial" value={device.serial} mono />
            <InfoRow label="Platform" value={device.platform} />
            <InfoRow label="OS Version" value={device.os_version} />
            <InfoRow label="Host" value={device.host?.hostname ?? "Unknown"} />
            <InfoRow
              label="ADB Connect"
              value={device.adb_tcp_port && device.host
                ? `${device.host.ip_address}:${device.adb_tcp_port}`
                : "Not available"}
              mono
            />
            <div style={infoRowStyle}>
              <span style={infoLabelStyle}>Status</span>
              <StatusBadge status={device.status} />
            </div>
            {device.status === "installing" && device.current_claim?.apk_name && (
              <div style={infoRowStyle}>
                <span style={infoLabelStyle}>APK</span>
                <span style={{ fontSize: "var(--font-size-base)", color: "var(--color-status-installing-text)", fontWeight: 600 }}>
                  Installing: {device.current_claim.apk_name}
                </span>
              </div>
            )}
            {(device.status === "ready" || device.status === "busy") && device.current_claim?.apk_name && (
              <div style={infoRowStyle}>
                <span style={infoLabelStyle}>APK</span>
                <span style={{ fontSize: "var(--font-size-base)", color: "var(--color-status-ready-text)", fontWeight: 600 }}>
                  Installed: {device.current_claim.apk_name}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Health Card */}
        <div style={cardStyle}>
          <h2 style={cardTitleStyle}>Health</h2>
          <div style={infoGridStyle}>
            <div style={infoRowStyle}>
              <span style={infoLabelStyle}>Battery</span>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-sm)" }}>
                <div
                  style={{
                    width: 80,
                    height: 8,
                    borderRadius: 4,
                    background: "var(--color-foreground)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${battery}%`,
                      height: "100%",
                      background: batteryColor,
                      borderRadius: 4,
                    }}
                  />
                </div>
                <span style={{ fontSize: "var(--font-size-sm)", fontWeight: 600 }}>
                  {battery}%
                </span>
              </div>
            </div>
            <InfoRow
              label="Last Heartbeat"
              value={device.last_heartbeat_at ? new Date(device.last_heartbeat_at).toLocaleString() : "Never"}
            />
            <InfoRow label="Network" value={device.has_network ? "OK" : "No Network"} />
            <InfoRow label="Failed Attempts" value={String(device.failed_attempts)} />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: "var(--space-md)", marginBottom: "var(--space-xl)" }}>
        <ActionButton
          label="Reboot"
          color="var(--color-primary)"
          disabled={device.status === "busy" || device.status === "offline" || device.status === "needs_cleanup" || device.status === "claimed" || device.status === "installing" || device.status === "ready"}
          onClick={() => handleAction("reboot")}
        />
        <ActionButton
          label="Quarantine"
          color="var(--color-warning)"
          disabled={device.status !== "available"}
          onClick={() => handleAction("quarantine")}
        />
        <ActionButton
          label="Force Release"
          color="var(--color-warning)"
          disabled={device.status === "available" || device.status === "offline" || device.status === "maintenance"}
          onClick={() => {
            setReleaseError(null);
            setShowReleaseConfirm(true);
          }}
        />
        <ActionButton
          label="Clean"
          color="var(--color-info)"
          disabled={device.status === "busy" || device.status === "offline" || device.status === "needs_cleanup" || device.status === "claimed" || device.status === "installing" || device.status === "ready"}
          onClick={() => handleAction("cleanup")}
        />
        <ActionButton
          label={identifyState.status === "requesting" ? "Requesting…" : "Identify Me"}
          color="var(--color-success)"
          disabled={identifyState.status === "requesting"}
          onClick={handleIdentify}
        />
        {/* Permanent removal — for phones that have left the fleet
            (decommissioned, reassigned to a developer, etc.). Blocked while
            the device is busy/claimed; user must release first. */}
        <ActionButton
          label="Remove"
          color="var(--color-danger)"
          disabled={device.status === "busy" || device.status === "claimed" || device.status === "installing" || device.status === "ready"}
          onClick={() => {
            setRemoveError(null);
            setShowRemoveConfirm(true);
          }}
        />
      </div>

      {identifyState.status === "complete" ? (
        <p role="status" aria-live="polite" style={{ marginTop: "calc(-1 * var(--space-md))", marginBottom: "var(--space-xl)" }}>
          {identifyState.outcome === "accepted" ? (
            <>Identify accepted · request {identifyState.requestId} · generation {identifyState.generation} · expires {new Date(identifyState.expiresAt).toLocaleTimeString()}</>
          ) : identifyState.outcome === "busy" ? (
            "Identify unavailable: the test app owns the phone foreground."
          ) : identifyState.outcome === "offline" ? (
            "Identify unavailable: the phone is unreachable."
          ) : (
            "Identify unavailable: the status companion is not supported."
          )}
        </p>
      ) : identifyState.status === "failed" ? (
        <p role="alert" style={{ color: "var(--color-error)", marginTop: "calc(-1 * var(--space-md))", marginBottom: "var(--space-xl)" }}>
          {identifyState.message}
        </p>
      ) : null}

      {/* APK install log for the most recent claim */}
      <DeviceInstallLog deviceId={device.id} />

      {/* Per-device activity feed — claims, releases, status, install,
          executions, anomalies. Live via SSE, 24h history. */}
      <DeviceActivityFeed deviceId={device.id} />

      {/* Recent Test History */}
      <div style={cardStyle}>
        <h2 style={cardTitleStyle}>Recent Test History</h2>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--color-foreground)" }}>
              {["Run #", "Scenario", "Result", "Started"].map((h) => (
                <th key={h} style={thStyle}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(device.recent_executions ?? []).map((exec) => (
              <tr key={exec.id} style={{ borderBottom: "var(--border-default)" }}>
                <td style={tdStyle}>
                  <Link to={`/runs/${exec.test_run_id}`} style={{ color: "var(--color-info)" }}>
                    View Run
                  </Link>
                </td>
                <td style={tdStyle}>{exec.scenario_name}</td>
                <td style={tdStyle}>
                  <StatusBadge status={exec.status} />
                </td>
                <td style={{ ...tdStyle, color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)" }}>
                  {new Date(exec.created_at).toLocaleString()}
                </td>
              </tr>
            ))}
            {(device.recent_executions ?? []).length === 0 && (
              <tr>
                <td colSpan={4} style={{ ...tdStyle, color: "var(--color-text-secondary)", textAlign: "center" }}>
                  No recent tests.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Claim History */}
      <DeviceClaimHistory deviceId={device.id} />
    </div>
  );
}

interface InstallLogResponse {
  claim_id: string | null;
  device_id?: string;
  job_id?: string;
  install_status?: string;
  install_error?: string | null;
  log: LogLine[];
  claimed_at?: string;
  released_at?: string | null;
}

function DeviceInstallLog({ deviceId }: { deviceId: string }) {
  const { data, refetch } = useApi<InstallLogResponse>(
    `/api/devices/${deviceId}/latest-install-log`
  );
  // Refetch when this device's claim status changes (install_complete fires
  // a device:status update with status=ready or available).
  useSSERefetch(
    "/api/events",
    ["device:status", "device:install_complete", "device:install_failed"],
    refetch
  );

  if (!data || !data.claim_id) return null;

  const subtitle = [
    data.job_id ? `job ${data.job_id}` : null,
    data.install_status ? `status: ${data.install_status}` : null,
    data.install_error ? `error: ${data.install_error}` : null
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div style={{ marginTop: "var(--space-xl)" }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 6,
          color: "var(--color-text-secondary)",
          fontSize: "var(--font-size-xs)"
        }}
      >
        <span>Most recent install · {subtitle || "—"}</span>
        {data.claimed_at && <span>{new Date(data.claimed_at).toLocaleString()}</span>}
      </div>
      <LogPanel
        title="APK install log"
        lines={data.log ?? []}
        emptyHint={
          data.install_status === "none"
            ? "This claim didn't request an APK install."
            : "Agent hasn't reported install progress yet — refresh in a moment."
        }
        height={300}
      />
    </div>
  );
}

interface ClaimEntry {
  id: string;
  job_id: string;
  pipeline_id: string | null;
  merge_request_iid: string | null;
  claimed_by_email: string | null;
  claim_source: string;
  reason: string | null;
  install_status: string;
  claimed_at: string;
  released_at: string | null;
}

function DeviceClaimHistory({ deviceId }: { deviceId: string }) {
  const { data, loading } = useApi<{ claims: ClaimEntry[] }>(`/api/devices/${deviceId}/claims`);
  const claims = data?.claims ?? [];

  return (
    <div style={{ ...cardStyle, marginTop: "var(--space-xl)" }}>
      <h2 style={cardTitleStyle}>Claim History</h2>
      {loading ? (
        <p style={{ color: "var(--color-text-secondary)" }}>Loading...</p>
      ) : claims.length === 0 ? (
        <p style={{ color: "var(--color-text-secondary)" }}>No claims recorded for this device.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--color-foreground)" }}>
              {["Source", "Who", "Reason / Job", "Claimed", "Duration", "State"].map((h) => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {claims.map((c) => {
              const start = new Date(c.claimed_at).getTime();
              const end = c.released_at ? new Date(c.released_at).getTime() : Date.now();
              const secs = Math.round((end - start) / 1000);
              const dur = secs < 60 ? `${secs}s` : secs < 3600 ? `${Math.round(secs / 60)}m` : `${Math.round(secs / 3600)}h`;
              return (
                <tr key={c.id} style={{ borderBottom: "var(--border-default)" }}>
                  <td style={tdStyle}>
                    <span style={{
                      padding: "2px 8px",
                      borderRadius: "var(--radius-pill)",
                      fontSize: 11,
                      fontWeight: 600,
                      background: c.claim_source === "cli" ? "var(--color-status-available-bg)" : "var(--color-status-busy-bg)",
                      color: c.claim_source === "cli" ? "var(--color-status-available-text)" : "var(--color-status-busy-text)"
                    }}>
                      {c.claim_source}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {c.claimed_by_email ?? <span style={{ color: "var(--color-text-secondary)" }}>(pipeline)</span>}
                  </td>
                  <td style={{ ...tdStyle, fontSize: "var(--font-size-sm)" }}>
                    {c.reason ? c.reason : c.merge_request_iid ? `MR !${c.merge_request_iid}` : c.job_id}
                  </td>
                  <td style={{ ...tdStyle, color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)" }}>
                    {new Date(c.claimed_at).toLocaleString()}
                  </td>
                  <td style={tdStyle}>{dur}</td>
                  <td style={tdStyle}>
                    {c.released_at ? (
                      <span style={{ color: "var(--color-text-secondary)" }}>released</span>
                    ) : (
                      <span style={{ color: "var(--color-warning)", fontWeight: 600 }}>active</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ConfirmReleaseModal({
  device,
  error,
  onCancel,
  onConfirm
}: {
  device: DeviceInfo;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [submitting, setSubmitting] = React.useState(false);
  const claim = device.current_claim;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-md)",
          padding: "var(--space-xl)",
          maxWidth: 480,
          width: "90%",
          boxShadow: "var(--shadow-card)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ ...cardTitleStyle, color: "var(--color-warning)", marginTop: 0 }}>
          Force release {device.model}?
        </h2>
        <p style={{ marginBottom: "var(--space-md)" }}>
          This will close the active claim and send the device through cleanup.
          Any in-flight test will be interrupted.
        </p>
        {claim && (
          <div
            style={{
              background: "var(--color-foreground)",
              padding: "var(--space-md)",
              borderRadius: "var(--radius-sm)",
              marginBottom: "var(--space-md)",
              fontSize: "var(--font-size-sm)"
            }}
          >
            <div><strong>Status:</strong> {device.status}</div>
            {claim.claimed_by && <div><strong>Claimed by:</strong> {claim.claimed_by}</div>}
            {claim.claimed_at && (
              <div>
                <strong>Claimed at:</strong> {new Date(claim.claimed_at).toLocaleString()}
              </div>
            )}
            {claim.apk_name && <div><strong>APK:</strong> {claim.apk_name}</div>}
          </div>
        )}
        {error && (
          <p style={{ color: "var(--color-danger)", marginBottom: "var(--space-md)" }}>
            {error}
          </p>
        )}
        <div style={{ display: "flex", gap: "var(--space-md)", justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: "var(--space-sm) var(--space-lg)",
              border: "var(--border-default)",
              borderRadius: "var(--radius-sm)",
              background: "var(--color-surface)",
              color: "var(--color-text-primary)",
              cursor: submitting ? "not-allowed" : "pointer",
              minHeight: "var(--touch-target-min)"
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            style={{
              padding: "var(--space-sm) var(--space-lg)",
              border: "none",
              borderRadius: "var(--radius-sm)",
              background: "var(--color-warning)",
              color: "var(--color-surface)",
              fontWeight: 600,
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.6 : 1,
              minHeight: "var(--touch-target-min)"
            }}
          >
            {submitting ? "Releasing..." : "Force Release"}
          </button>
        </div>
      </div>
    </div>
  );
}

// AIDEV-NOTE: Type-to-confirm pattern for the destructive Remove action.
// Stronger than the modal-only confirm we use for Force Release because
// Remove is irreversible (deletes the device row + breaks historical
// references). User must type the device's exact serial number; the
// Confirm button stays disabled until it matches.
function ConfirmRemoveModal({
  device,
  error,
  onCancel,
  onConfirm
}: {
  device: DeviceInfo;
  error: string | null;
  onCancel: () => void;
  onConfirm: (typedSerial: string) => void;
}) {
  const [submitting, setSubmitting] = React.useState(false);
  const [typed, setTyped] = React.useState("");
  const matches = typed === device.serial;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(typed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-md)",
          padding: "var(--space-xl)",
          maxWidth: 480,
          width: "90%",
          boxShadow: "var(--shadow-card)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ ...cardTitleStyle, color: "var(--color-danger)", marginTop: 0 }}>
          Remove {device.model} from the fleet?
        </h2>
        <p style={{ marginBottom: "var(--space-md)" }}>
          This permanently deletes the device record. Use this only when the
          phone has physically left the fleet — decommissioned, reassigned to
          a developer, or replaced. Historical test runs that referenced this
          device will keep their data but the device link will dangle.
        </p>
        <p style={{ marginBottom: "var(--space-md)", fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>
          To confirm, type the device's serial number: <strong style={{ fontFamily: "var(--font-mono)" }}>{device.serial}</strong>
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={device.serial}
          autoFocus
          style={{
            width: "100%",
            padding: "var(--space-sm)",
            fontSize: "var(--font-size-base)",
            fontFamily: "var(--font-mono)",
            border: "var(--border-default)",
            borderRadius: "var(--radius-sm)",
            background: "var(--color-foreground)",
            color: "var(--color-text-primary)",
            marginBottom: "var(--space-md)",
            boxSizing: "border-box"
          }}
        />
        {error && (
          <p style={{ color: "var(--color-danger)", marginBottom: "var(--space-md)" }}>
            {error}
          </p>
        )}
        <div style={{ display: "flex", gap: "var(--space-md)", justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: "var(--space-sm) var(--space-lg)",
              border: "var(--border-default)",
              borderRadius: "var(--radius-sm)",
              background: "var(--color-surface)",
              color: "var(--color-text-primary)",
              cursor: submitting ? "not-allowed" : "pointer",
              minHeight: "var(--touch-target-min)"
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || !matches}
            style={{
              padding: "var(--space-sm) var(--space-lg)",
              border: "none",
              borderRadius: "var(--radius-sm)",
              background: "var(--color-danger)",
              color: "var(--color-surface)",
              fontWeight: 600,
              cursor: submitting || !matches ? "not-allowed" : "pointer",
              opacity: submitting || !matches ? 0.5 : 1,
              minHeight: "var(--touch-target-min)"
            }}
          >
            {submitting ? "Removing..." : "Remove permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={infoRowStyle}>
      <span style={infoLabelStyle}>{label}</span>
      <span
        style={{
          fontSize: "var(--font-size-base)",
          fontFamily: mono ? "var(--font-mono)" : "inherit",
        }}
      >
        {value}
      </span>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: "var(--color-surface)",
  borderRadius: "var(--radius-md)",
  boxShadow: "var(--shadow-card)",
  padding: "var(--space-lg)",
};

const cardTitleStyle: React.CSSProperties = {
  fontSize: "var(--font-size-lg)",
  fontWeight: 600,
  color: "var(--color-primary)",
  marginBottom: "var(--space-md)",
};

const infoGridStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-sm)",
};

const infoRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "var(--space-xs) 0",
};

const infoLabelStyle: React.CSSProperties = {
  fontSize: "var(--font-size-sm)",
  color: "var(--color-text-secondary)",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

function ActionButton({ label, color, disabled, onClick }: { label: string; color: string; disabled: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "var(--space-sm) var(--space-lg)",
        border: "none",
        borderRadius: "var(--radius-sm)",
        background: disabled ? "var(--color-neutral)" : color,
        color: "var(--color-surface)",
        fontSize: "var(--font-size-base)",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        minHeight: "var(--touch-target-min)",
      }}
    >
      {label}
    </button>
  );
}


const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "var(--space-sm) var(--space-md)",
  fontSize: "var(--font-size-sm)",
  fontWeight: 600,
  color: "var(--color-text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const tdStyle: React.CSSProperties = {
  padding: "var(--space-sm) var(--space-md)",
  fontSize: "var(--font-size-base)",
  verticalAlign: "middle",
};
